import React, { FC, useCallback, useMemo, useState } from 'react';

import cn from 'classnames';
import { useParams } from 'react-router-dom';

import { COMPANIES_INVITE_MODAL_TITLE } from 'constants/companies';
import { ROUTES } from 'constants/routes';
import { ButtonVariants } from 'constants/shared/button';
import { TypographyVariants } from 'constants/shared/typography';
import { InviteUserType, UserTypes } from 'constants/user';
import { useInviteInvestorsPreviewEmail } from 'hooks/investors/useInviteInvestorsPreviewEmail';
import useModal from 'hooks/use-modal/useModal';
import { useCanInviteInvestors } from 'hooks/useCanInviteInvestors';
import { InvitePreviewModalParams, InvitePreviewTemplate } from 'interfaces';
import { getCompanyById } from 'modules/companies/action';
import {
  selectCanStartDeal,
  selectCompanyDealParametersHasActiveDeal,
  selectCurrentCompanyData,
  selectCurrentCompanyId,
  selectCurrentCompanyMainInfo,
  selectCurrentCompanyName,
} from 'modules/companies/selectors';
import { getCompanyIacDeal } from 'modules/company-iac/action';
import { selectCompanyIac, selectCompanyShareClasses } from 'modules/company-iac/selectors';
import { selectIsActionLoading } from 'modules/current-user/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { CompanyIacStartDealModal } from 'page-components/company-iac';
import AddExternalShareExchangeModal from 'page-components/deals/share-exchanges-extarnals/AddExternalShareExchangeModal';
import { Button, ModalWindow } from 'shared-components';
import Typography from 'shared-components/Typography';
import { checkIsAdmin, getArchivedCompanyIacLink, getArchivedDueDiligenceLink, navigateWithFilter } from 'utils';

import CompanyInviteInvestorsPreviewEmail from '../../company-investors/CompanyInviteInvestorsPreviewEmail';
import { CompanyProfileInvite, MyInvitesModalForm } from '../../index';
import AdminCompanyProfileRelatedItem from './AdminCompanyProfileRelatedItem';
import AdminCompanyProfileRelatedSectionPreloader from './AdminCompanyProfileRelatedSectionPreloader';

interface Props {
  isLoading: boolean;
}

const RELATED_ITEM_SHARED_BUTTON_CLASSNAME = 'w-full !2xl:w-[160px]';

const AdminCompanyProfileRelatedSection: FC<Props> = ({ isLoading }) => {
  const dispatch = useAppDispatch();
  const { id: companyId } = useParams();
  const id = useAppSelector(selectCurrentCompanyId);
  const canStartDeal = useAppSelector(selectCanStartDeal);
  const isInvitesActionLoading = useAppSelector(selectIsActionLoading);
  const hasCompanyActiveDeal = useAppSelector(selectCompanyDealParametersHasActiveDeal);
  const shareClasses = useAppSelector(selectCompanyShareClasses);

  const companyName = useAppSelector(selectCurrentCompanyName);
  const { data } = useAppSelector(selectCurrentCompanyData);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const companyIac = useAppSelector(selectCompanyIac);
  const { canViewInvestmentProposal } = useAppSelector(selectCurrentCompanyMainInfo);

  const checkedFullyDilutedShareCapitalClasses = (companyIac?.fullyDilutedShareCapital || [])
    .filter((item) => item.checked)
    .map((item) => item.classOfShares);

  const filteredSharePrices = (companyIac?.companySharePrices || []).filter((price) =>
    checkedFullyDilutedShareCapitalClasses.includes(price.classOfShares),
  );

  const sharesToShowInLetter = canViewInvestmentProposal ? filteredSharePrices : [];

  const pricesAndClassesList =
    sharesToShowInLetter.map(({ classOfShares, sharePrice }) => `£${sharePrice} per ${classOfShares}`).join(', ') + '.';

  const pricesAndClasses =
    sharesToShowInLetter.length !== 0
      ? `The offer price is ${pricesAndClassesList} Please note - the deal is on an exchange of shares basis - not cash.`
      : '';

  const numberSharesToPurchase = companyIac?.sharesToPurchase?.noOfSharesPurchaseAtCompletion;
  const sharesToPurchase = numberSharesToPurchase && canViewInvestmentProposal ? `${numberSharesToPurchase}` : '24.99%';

  const { onOpen: onOpenStartDealModal, ...startDealModalProps } = useModal();
  const {
    isOpen: isOpenCreateExternalShareExchangeModal,
    onOpen: onOpenCreateExternalShareExchangeModal,
    onClose: onCloseCreateExternalShareExchangeModal,
    ...createExternalShareExchangeModalProps
  } = useModal();

  const [previewEmailModalState, handleOpenInvitePreviewModal, isPreviewEmailLoading] =
    useInviteInvestorsPreviewEmail();

  const canInviteInvestors = useCanInviteInvestors(data);

  const isAdmin = useMemo(() => checkIsAdmin(), []);

  const loadCompany = useCallback(() => {
    if (!companyId) {
      return;
    }

    dispatch(getCompanyById({ id: companyId, isAdmin: true }));
  }, [companyId, dispatch]);

  const onStartDeal = useCallback(() => {
    if (!isAdmin || !companyId) {
      return;
    }

    setIsButtonLoading(true);

    dispatch(getCompanyIacDeal(companyId))
      .unwrap()
      .then(() => onOpenStartDealModal())
      .finally(() => setIsButtonLoading(false));
  }, [isAdmin, companyId, dispatch, onOpenStartDealModal]);

  const companyProfileRelatedItems = useMemo(() => {
    if (!id) return [];

    return [
      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Share Exchange Requests',
        description: 'View all Share Exchange Requests',
        linkPath: navigateWithFilter(true, ROUTES.shareExchangeRequests, 'company_id', id.toString()),
      },
      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Associated Founders/Directors',
        description: 'View all Associated Founders',
        linkPath: navigateWithFilter(true, ROUTES.founders, 'company', id.toString()),
      },
      {
        title: 'Generate Investor letter',
        node: (
          <>
            <ModalWindow
              title={COMPANIES_INVITE_MODAL_TITLE}
              titleVariant={TypographyVariants.MEDIUM}
              headerClassName='text-blue-800'
              className='min-w-[70%] 2xl:min-w-[1048px]'
              contentClassName='overflow-x-hidden'
              wrapperClassName='pt-6'
              {...previewEmailModalState}
            >
              <CompanyInviteInvestorsPreviewEmail
                isLoading={isPreviewEmailLoading || isLoading}
                companyName={companyName}
                handleSetIsLoading={() => ''}
                fundName={companyIac?.fundName || ''}
                pricesAndClasses={pricesAndClasses}
                sharesToPurchase={sharesToPurchase}
                onClose={previewEmailModalState.onClose}
                {...(previewEmailModalState.modalProps as InvitePreviewModalParams & {
                  templates: InvitePreviewTemplate;
                })}
                downloadOnSubmit
                notEditable
              />
            </ModalWindow>
            <Button
              isLoading={isPreviewEmailLoading}
              onClick={() =>
                handleOpenInvitePreviewModal({
                  companyId: String(companyId),
                  companyName,
                  fundName: companyIac?.fundName,
                  pricesAndClasses: pricesAndClasses,
                  sharesToPurchase: sharesToPurchase,
                  selectedInvestors: [],
                  shouldBeMocked: true,
                })
              }
              variant={ButtonVariants.SECONDARY}
              className={RELATED_ITEM_SHARED_BUTTON_CLASSNAME}
            >
              View
            </Button>
          </>
        ),
      },
      {
        title: 'Invite Investors',
        node: (
          <MyInvitesModalForm
            companyId={id}
            buttonClassName={cn(RELATED_ITEM_SHARED_BUTTON_CLASSNAME, 'mt-0')}
            loading={isInvitesActionLoading}
            type={UserTypes.INVESTOR}
            inviteType={InviteUserType.INVESTOR_TO_COMPANY}
            canInviteInvestors={canInviteInvestors}
          />
        ),
        children: (
          <div className={cn('flex flex-col', { 'mt-4': !hasCompanyActiveDeal })}>
            {hasCompanyActiveDeal ? (
              <Typography
                className={cn(
                  RELATED_ITEM_SHARED_BUTTON_CLASSNAME,
                  'w-full flex items-center justify-center self-end h-fit my-1',
                )}
              >
                Or
              </Typography>
            ) : null}
            <CompanyProfileInvite
              className='my-0 flex-col !2xl:flex-row gap-4 !2xl:gap-0'
              buttonClassName={RELATED_ITEM_SHARED_BUTTON_CLASSNAME}
              companyId={id}
              hasActiveDeal
              canInviteInvestors={canInviteInvestors}
            />
          </div>
        ),
      },
      ...(hasCompanyActiveDeal
        ? [
            {
              title: 'Invite Founders',
              node: (
                <MyInvitesModalForm
                  companyId={id}
                  buttonClassName={cn(RELATED_ITEM_SHARED_BUTTON_CLASSNAME, 'mt-0')}
                  loading={isInvitesActionLoading}
                  type={UserTypes.FOUNDER}
                />
              ),
            },
          ]
        : []),
      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Associated Investors',
        description: 'View all Associated Founders',
        linkPath: navigateWithFilter(true, ROUTES.investors, 'company', id.toString()),
      },
      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Archived Due Diligence',
        description: 'View all Archived Due Diligence',
        buttonText: 'View',
        linkPath: getArchivedDueDiligenceLink(id, isAdmin),
      },

      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Archived IAC',
        description: 'View all Archived IAC',
        buttonText: 'View',
        linkPath: getArchivedCompanyIacLink(id, isAdmin),
      },
      {
        buttonVariant: ButtonVariants.LINK,
        title: 'Off Platform Share Exchange',
        description: '',
        node: (
          <>
            <AddExternalShareExchangeModal
              companyId={companyId}
              companyName={companyName || ''}
              isOpen={isOpenCreateExternalShareExchangeModal}
              onClose={onCloseCreateExternalShareExchangeModal}
              title={'Add Off Platform Share Exchange'}
              shareClasses={shareClasses}
              {...createExternalShareExchangeModalProps}
            />
            <Button
              isLoading={isPreviewEmailLoading}
              onClick={() => onOpenCreateExternalShareExchangeModal()}
              variant={ButtonVariants.SECONDARY}
              className={RELATED_ITEM_SHARED_BUTTON_CLASSNAME}
            >
              Add
            </Button>
          </>
        ),
      },

      {
        buttonVariant: ButtonVariants.SECONDARY,
        disabled: !canStartDeal,
        title: 'Deal Information',
        buttonText: 'Start New Deal',
        isLoading: isButtonLoading,
        action: onStartDeal,
      },
    ];
  }, [
    isPreviewEmailLoading,
    canStartDeal,
    companyId,
    hasCompanyActiveDeal,
    id,
    isAdmin,
    isButtonLoading,
    isInvitesActionLoading,
    isLoading,
    onStartDeal,
    previewEmailModalState,
    canInviteInvestors,
    companyName,
    handleOpenInvitePreviewModal,
    createExternalShareExchangeModalProps,
    isOpenCreateExternalShareExchangeModal,
    onCloseCreateExternalShareExchangeModal,
    onOpenCreateExternalShareExchangeModal,
  ]);

  return (
    <>
      <CompanyIacStartDealModal companyId={companyId} onSuccessCallback={loadCompany} {...startDealModalProps} />

      <div className='w-full md:w-[35%] flex flex-col'>
        {isLoading ? (
          <AdminCompanyProfileRelatedSectionPreloader />
        ) : (
          <>
            {companyProfileRelatedItems.map((item, index) => (
              <AdminCompanyProfileRelatedItem key={`${item.title}-${index}`} {...item} />
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default AdminCompanyProfileRelatedSection;
